/**
 * Knowledge Assistant — the access-filtered retrieval workbench (V3 screen 18).
 *
 * Spec: specs/v3/product-technical/18-knowledge-assistant.md. The user starts
 * from a *question* and receives an answer composed over an access-filtered
 * Context Packet, with every source/trace shown and any proposed next action
 * surfaced as an Action Card the human must accept — never an in-place mutation.
 *
 * Lens/projection doctrine: this screen is a READ projection that happens to
 * speak natural language. It is wired to the shippable assistant lane
 * (`POST /api/assistant/chat` with `stream:false`), which runs the boxed
 * tool-calling loop server-side (ContextResolver → AccessGuard → structured
 * graph lookup), writes one `agent_runs` row per turn, and returns the answer
 * plus the auto-executed read tools (the sources/trace) and any pending
 * proposals. The assistant proposes; it never writes business truth here.
 *
 * The screen body routes through the shared eight-state `RenderState` contract
 * (S13): loading while a turn is in flight, empty before the first question, an
 * inline error for a failed turn, a `forbidden` downgrade when a turn is denied
 * by policy (HTTP 403, with the approval path), and a `redacted` disclosure when
 * the turn reports that a source was withheld — its content never leaked.
 */
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Icon } from "@/components/ui/icon";
import {
  RenderState,
  type RenderStateKind,
} from "@/components/app/RenderState";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Field,
  FieldGrid,
  InspectorSection,
} from "@/components/inspector";
import { api, ApiError } from "@/lib/api";
import { humanize } from "@/lib/format";

/** One auto-executed read tool surfaced as a cited source / trace step. */
interface AssistantToolResult {
  tool: string;
  args: unknown;
  result?: unknown;
  error?: string;
}

/** A proposal the assistant prepared, awaiting human Approve/Reject. */
interface AssistantPendingProposal {
  tool: string;
  proposal_id?: string;
  [key: string]: unknown;
}

/** The `stream:false` body of `POST /api/assistant/chat` (assistant turn). */
interface AssistantTurnResult {
  session_id: string;
  agent_run_id: string;
  reply: string;
  tool_results: AssistantToolResult[];
  pending_proposals: AssistantPendingProposal[];
}

/** Scope a question is asked within. `context_key` is the assistant-session key. */
interface AssistantScope {
  /** UI label. */
  label: string;
  /** `user:<id>` | `dept:<id>` | `project:<id>` — the session key the API wants. */
  contextKey: string;
}

/**
 * The default scope: the signed-in user's own authorized view. The standalone
 * workbench can be widened to a department/project later; the wire shape is the
 * same `context_key`, so widening is a label change, not a new contract.
 */
const DEFAULT_SCOPE: AssistantScope = {
  label: "My authorized view",
  contextKey: "user:me",
};

/** A turn the user asked, paired with its answer (or in-flight/failed state). */
interface AssistantTurn {
  id: string;
  question: string;
  status: "pending" | "answered" | "error" | "forbidden";
  result: AssistantTurnResult | null;
  error: unknown;
}

let turnSeq = 0;
function nextTurnId(): string {
  turnSeq += 1;
  return `turn-${turnSeq}`;
}

/** Map a turn's status to the eight-state kind for its answer card body. */
function turnState(turn: AssistantTurn): RenderStateKind {
  switch (turn.status) {
    case "pending":
      return "loading";
    case "forbidden":
      return "forbidden";
    case "error":
      return "error";
    case "answered":
    default:
      // A turn that withheld a source by policy is a `redacted` disclosure; the
      // content itself is never rendered (the tool result carries only the
      // safe redaction summary the server allowed).
      return turn.result && hasRedactedSource(turn.result) ? "redacted" : "normal";
  }
}

/** A turn reports a withheld source when a read tool returned an access error. */
function hasRedactedSource(result: AssistantTurnResult): boolean {
  return result.tool_results.some(
    (entry) =>
      typeof entry.error === "string" &&
      /forbidden|denied|redact|not authori|access/i.test(entry.error),
  );
}

/** A single cited source / trace step (one auto-executed read tool). */
function SourceRow({ entry }: { entry: AssistantToolResult }) {
  return (
    <div
      data-testid="assistant-source"
      className="rounded-md border border-border bg-muted/30 px-3 py-2"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Icon name="tool" aria-hidden />
          {humanize(entry.tool)}
        </div>
        {entry.error ? (
          <Badge variant="outline">
            <Icon name="roles" aria-hidden />
            Withheld
          </Badge>
        ) : (
          <Badge variant="secondary">
            <Icon name="resultOk" aria-hidden />
            Read
          </Badge>
        )}
      </div>
    </div>
  );
}

/** The answer + sources/trace + proposals for one answered turn. */
function AnsweredTurn({ turn }: { turn: AssistantTurn }) {
  const result = turn.result;
  if (!result) return null;
  return (
    <div className="flex flex-col gap-4">
      <p
        data-testid="assistant-answer"
        className="text-sm leading-relaxed [overflow-wrap:anywhere]"
      >
        {result.reply}
      </p>

      <FieldGrid>
        <Field label="Sources">{result.tool_results.length}</Field>
        <Field label="Proposed actions">{result.pending_proposals.length}</Field>
        <Field label="Agent run">
          <span className="font-mono text-xs [overflow-wrap:anywhere]">
            {result.agent_run_id}
          </span>
        </Field>
      </FieldGrid>

      {result.tool_results.length > 0 ? (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-muted-foreground">
            Sources &amp; trace
          </p>
          {result.tool_results.map((entry, index) => (
            <SourceRow key={`${entry.tool}-${index}`} entry={entry} />
          ))}
        </div>
      ) : null}

      {result.pending_proposals.length > 0 ? (
        <div
          data-testid="assistant-proposals"
          className="rounded-md border border-border bg-muted/30 px-3 py-2"
        >
          <div className="mb-1 flex items-center gap-2 text-sm font-medium">
            <Icon name="actionCards" aria-hidden />
            Proposed actions awaiting approval
          </div>
          <p className="text-xs text-muted-foreground">
            {result.pending_proposals.length} proposal
            {result.pending_proposals.length === 1 ? "" : "s"} — the assistant
            proposes; a human accepts or rejects. Nothing is applied here.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/** One question/answer card in the thread. */
function TurnCard({ turn }: { turn: AssistantTurn }) {
  return (
    <Card data-testid="assistant-turn">
      <CardHeader className="p-4 pb-2">
        <div className="flex items-start gap-2">
          <Icon name="user" className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
          <CardTitle className="text-base [overflow-wrap:anywhere]">
            {turn.question}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <RenderState
          state={turnState(turn)}
          loadingRows={2}
          error={turn.error}
          forbiddenReason="This question was denied by access policy."
          forbiddenApprovalPath="the context owner or an administrator"
          redactedMessage="The answer omits a source you are not authorized to see; its content is withheld."
        >
          <AnsweredTurn turn={turn} />
        </RenderState>
      </CardContent>
    </Card>
  );
}

/**
 * The standalone Knowledge Assistant workbench. Answer-first: the thread is the
 * center, sources are pulled into each answer card, and the composer sits at the
 * bottom. Before the first question the whole screen is the shared `empty` state.
 */
export function KnowledgeAssistant() {
  const [scope] = React.useState<AssistantScope>(DEFAULT_SCOPE);
  const [question, setQuestion] = React.useState("");
  const [turns, setTurns] = React.useState<AssistantTurn[]>([]);
  const [sessionId, setSessionId] = React.useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = React.useState(false);

  const ask = React.useCallback(
    async (raw: string) => {
      const text = raw.trim();
      if (!text || submitting) return;
      const id = nextTurnId();
      setSubmitting(true);
      setQuestion("");
      setTurns((prev) => [
        ...prev,
        { id, question: text, status: "pending", result: null, error: null },
      ]);

      try {
        const result = await api.post<AssistantTurnResult>("/assistant/chat", {
          context_key: scope.contextKey,
          message: text,
          session_id: sessionId,
          stream: false,
        });
        setSessionId(result.session_id);
        setTurns((prev) =>
          prev.map((turn) =>
            turn.id === id
              ? { ...turn, status: "answered", result, error: null }
              : turn,
          ),
        );
      } catch (err) {
        const forbidden = err instanceof ApiError && err.status === 403;
        setTurns((prev) =>
          prev.map((turn) =>
            turn.id === id
              ? {
                  ...turn,
                  status: forbidden ? "forbidden" : "error",
                  result: null,
                  error: err,
                }
              : turn,
          ),
        );
      } finally {
        setSubmitting(false);
      }
    },
    [scope.contextKey, sessionId, submitting],
  );

  return (
    <div className="max-w-5xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Knowledge Assistant"
          subtitle="Ask a question; get an answer composed over your access-filtered authorized truth, with every source, trace, and proposed action shown."
          icon="assistant"
        />

        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline">
            <Icon name="scope" aria-hidden />
            {scope.label}
          </Badge>
          <Badge variant="secondary">
            <Icon name="approve" aria-hidden />
            Access-filtered
          </Badge>
          <span className="font-mono">{scope.contextKey}</span>
        </div>

        <InspectorSection title="Conversation">
          {turns.length === 0 ? (
            <RenderState
              state="empty"
              empty={{
                icon: "assistantChat",
                title: "Ask the first question.",
                hint: "The assistant answers from authorized records only — every source and trace is shown, and any next action is a proposal you approve.",
              }}
            />
          ) : (
            <div className="flex flex-col gap-3">
              {turns.map((turn) => (
                <TurnCard key={turn.id} turn={turn} />
              ))}
            </div>
          )}
        </InspectorSection>

        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void ask(question);
          }}
        >
          <label htmlFor="assistant-question" className="text-sm font-medium">
            Question
          </label>
          <Textarea
            id="assistant-question"
            aria-label="Question"
            placeholder="Ask about contexts, artifacts, decisions, claims, evidence, packages, or traces…"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                event.preventDefault();
                void ask(question);
              }
            }}
            rows={3}
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              The assistant proposes; it never mutates business state.
            </p>
            <Button type="submit" disabled={submitting || question.trim().length === 0}>
              <Icon name="send" data-icon="inline-start" aria-hidden />
              {submitting ? "Asking…" : "Ask"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default KnowledgeAssistant;
